// Web push on this device. iPhones only support it from the home-screen app
// (Share → Add to Home Screen), so the Alerts page checks for that first.

import { subscribeDevice, unsubscribeDevice } from "../api/alerts";

export function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function isIOS() {
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

export function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true
  );
}

function keyToBytes(base64url) {
  const padded = base64url + "=".repeat((4 - (base64url.length % 4)) % 4);
  const raw = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

export async function currentSubscription() {
  if (!pushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration("/");
  return reg ? reg.pushManager.getSubscription() : null;
}

/**
 * Ask permission, subscribe, and register the device with the server.
 * Call straight from a tap: Safari only shows the permission prompt for a
 * user gesture, so nothing slow may run before requestPermission().
 */
export async function enablePush(publicKey) {
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error(
      permission === "denied"
        ? "Notifications are blocked for this app. Turn them on in your phone's Settings → Notifications → Birdie Bus."
        : "Notifications weren't allowed."
    );
  }
  const reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  await navigator.serviceWorker.ready;
  const key = keyToBytes(publicKey);
  let sub = await reg.pushManager.getSubscription();
  if (sub) {
    // A subscription made with a different server key can't receive ours.
    const existing = sub.options?.applicationServerKey;
    const same = existing && new Uint8Array(existing).every((b, i) => b === key[i]);
    if (!same) {
      await sub.unsubscribe();
      sub = null;
    }
  }
  if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
  await subscribeDevice(sub.toJSON());
  return sub;
}

export async function disablePush() {
  const sub = await currentSubscription();
  if (!sub) return;
  try {
    await unsubscribeDevice(sub.endpoint);
  } finally {
    await sub.unsubscribe();
  }
}
